/**
 * Parking-area tiers by rank (CS-16922 / CS-17024). Pure functions: no DOM, no jQuery.
 * @typedef {{id: string, name: string, rank?: (number|null)}} Area
 */

export const MORE_VALUE = "__cs17024_more__";

/**
 * «Mostra altro (N)»: the number of areas the entry adds is composed outside the translated text, so every catalog
 * keeps the single «Mostra altro» msgid and the number needs no plural forms.
 *
 * @param {string} label the translated «Mostra altro»
 * @param {number} hiddenCount how many entries the click adds
 * @returns {string}
 */
export function showMoreLabel(label, hiddenCount) {
    return `${label} (${hiddenCount})`;
}

/**
 * Message under a selector whose booking's area is no longer offered: «<name> non è più disponibile, selezionane un
 * altro» when the page could get the name, else the generic message. The name goes in by plain replacement, not
 * through the catalog's printf, so it is shown exactly as it is.
 *
 * @param {string} namedMessage the translated «%s non è più disponibile, selezionane un altro»
 * @param {string} genericMessage the translated message without the name
 * @param {string} areaName empty when the name is unknown
 * @returns {string}
 */
export function missingAreaMessage(namedMessage, genericMessage, areaName) {
    return areaName ? namedMessage.replace("%s", () => areaName) : genericMessage;
}

/**
 * Areas of the options the server rendered, in their order. data-rank keeps the three states of the API rank: a
 * number, empty for null (behind everything), absent when the API sent no rank (no rank axis). The empty placeholder
 * option is not an area.
 *
 * @param {Iterable<{value: string, textContent: string, dataset: Object<string, string>}>} options
 * @returns {Area[]}
 */
export function areasFromOptions(options) {
    return Array.from(options)
        .filter((option) => option.value !== "")
        .map((option) => {
            const area = {id: option.value, name: option.textContent};
            if (Object.prototype.hasOwnProperty.call(option.dataset, "rank")) {
                area.rank = option.dataset.rank === "" ? null : Number(option.dataset.rank);
            }
            return area;
        });
}

/**
 * The data-rank value that areasFromOptions() reads back: the rank, "" for null, undefined for an area with no rank.
 * The adapters write it on the options they rebuild, so a later attach on the same select reads the same ranks.
 *
 * @param {Area} area
 * @returns {(string|undefined)}
 */
export function rankAttribute(area) {
    if (!Object.prototype.hasOwnProperty.call(area, "rank")) {
        return undefined;
    }
    return area.rank === null ? "" : String(area.rank);
}

/**
 * @param {Area[]} areas in server order
 * @param {boolean} rankEnabled parkingAreas.visibilityRankEnabled
 * @returns {{primary: Area[], others: Area[], showMore: boolean}}
 */
export function rankedTiers(areas, rankEnabled) {
    const numericRanks = areas
        .filter((area) => typeof area.rank === "number")
        .map((area) => area.rank);
    if (!rankEnabled || numericRanks.length === 0) {
        return {primary: [...areas], others: [], showMore: false};
    }
    const minimum = Math.min(...numericRanks);
    const primary = areas.filter((area) => area.rank === minimum);
    const others = areas.filter((area) => area.rank !== minimum);
    return {primary, others, showMore: others.length > 0};
}

/**
 * @param {{primary: Area[], others: Area[]}} tiers
 * @param {(string|null|undefined)} selectedId
 * @returns {"none"|"primary"|"secondary"|"missing"}
 */
export function selectionState(tiers, selectedId) {
    if (selectedId === null || selectedId === undefined || selectedId === "") {
        return "none";
    }
    const id = String(selectedId);
    if (tiers.primary.some((area) => area.id === id)) {
        return "primary";
    }
    return tiers.others.some((area) => area.id === id) ? "secondary" : "missing";
}

/**
 * Destination in rank tiers, like the app (search_booking_bloc _onParkingAreaSelected): the tiers come from the
 * allowed set only, so the primary tier is the lowest rank inside it. Each new allowed set starts collapsed, unless
 * the destination that ends up selected is in the others. A wanted area that the select offers but the set does not
 * allow falls back to the first area of the primary tier (a departure change prefers the departure, else that one).
 * A wanted area the select does not offer at all (the booking's destination, no longer offered to the user) is never
 * replaced silently: nothing is selected and missingId names it, as for the departure.
 *
 * @param {Area[]} areas every area of the select, in server order
 * @param {(Set<string>|null)} allowedIds the areas the destination offers now (A2B clusters); null = all of them
 * @param {boolean} rankEnabled parkingAreas.visibilityRankEnabled
 * @param {(string|null|undefined)} selectedId the destination wanted after this change
 * @returns {{tiers: {primary: Area[], others: Area[], showMore: boolean}, current: string, expanded: boolean,
 *          missingId: string}} missingId is the wanted id the select does not offer, else empty
 */
export function destinationTiers(areas, allowedIds, rankEnabled, selectedId) {
    const allowed = allowedIds === null ? areas : areas.filter((area) => allowedIds.has(area.id));
    const tiers = rankedTiers(allowed, rankEnabled);
    const state = selectionState(tiers, selectedId);
    if (state === "primary" || state === "secondary") {
        return {tiers, current: String(selectedId), expanded: state === "secondary", missingId: ""};
    }
    if (state === "missing" && !areas.some((area) => area.id === String(selectedId))) {
        return {tiers, current: "", expanded: false, missingId: String(selectedId)};
    }
    return {tiers, current: tiers.primary.length ? tiers.primary[0].id : "", expanded: false, missingId: ""};
}
